/**
 * Assignment, re-grade and the deadline — the only `PATCH` on this screen, and the
 * only operation that carries a precondition.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE PRECONDITION COMES FROM THE HEADER, VERBATIM
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `Incident.version` is **not populated in v1** — its absence means "not available",
 * never "this incident has never been edited". The concurrency token is `updated_at`
 * as PostgreSQL prints it,
 * `to_char(updated_at AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.US"Z"')`, to the
 * **microsecond**, compared inside the UPDATE's own `WHERE`.
 *
 * So this panel takes the token from the `ETag` **response header** of the read that
 * produced the incident on screen, and echoes it back unchanged. It never rebuilds it
 * from `incident.updated_at`, and it never passes it through a JavaScript `Date`:
 * `Date` has millisecond resolution, the last three digits vanish, and every
 * precondition would be refused `400` for being the wrong *shape* — the server checks
 * `\d{6}Z` and refuses rather than guessing, which is the correct behaviour and an
 * unhelpful one to trip over.
 *
 * **No ETag, no request.** The submit is absent — not disabled — when the read
 * carried no `ETag`, and the panel says the write is unavailable and why. A disabled
 * button here would assert a permission problem; the truth is that the client does
 * not hold a precondition it is required to send, and pressing anything would produce
 * a `400` naming a header the operator never typed.
 *
 * ── A stale token ──────────────────────────────────────────────────────────
 *
 * `409 WORKFORCE_VERSION_CONFLICT`, carrying `details.current_etag`. The panel
 * renders the server's own sentence, **keeps the operator's unsaved edit on screen**,
 * and asks the screen to re-read so the next attempt carries a live token. It does
 * **not** retry with `current_etag`: that is exactly the blind overwrite the header
 * exists to prevent, and the other person's change is the thing this operator has not
 * seen yet. API_CONTRACT §6 says the field is there "để client refetch", which is
 * what happens.
 *
 * ── Two fields that are id boxes, and one that is a picker ──────────────────
 *
 * `owner_id` is a `ResourceId` — this service's own `wp_employees.id`, not an achau
 * id. `GET /employees` (Task T2-A) and `EmployeePicker` (Task T2-B) exist now, but
 * this panel has not been rewired to use them, so it stays a typed id box with its
 * meaning stated, the same wall Tasks 43, 44 and 45 hit before either shipped.
 * `severity` *is* a picker, because its vocabulary is a closed five-value enum this
 * client can hold without inventing anything.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { AlertSeverity, IncidentPatchRequest } from '../../api/types';
import type { WorkforceApiError } from '../../api/errors';
import { currentEtag } from '../../api/useApiMutation';
import { ALERT_SEVERITIES } from '../../alerts/inbox';
import { SEVERITY_LABEL } from '../../incidents/labels';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

export interface AssignmentPanelProps {
  readonly ownerId: string | null;
  readonly severity: AlertSeverity;
  /** The `ETag` of the read that produced this incident. `null` means none arrived. */
  readonly etag: string | null;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly saved: boolean;
  readonly onSubmit: (body: IncidentPatchRequest) => void;
}

export function AssignmentPanel({
  ownerId,
  severity,
  etag,
  pending,
  error,
  saved,
  onSubmit,
}: AssignmentPanelProps): ReactNode {
  const { t } = useI18n();
  const [owner, setOwner] = useState(ownerId ?? '');
  const [grade, setGrade] = useState<AlertSeverity>(severity);

  const conflictEtag = error === null ? null : currentEtag(error);

  return (
    <section className="wp-section" aria-labelledby="l5-assign-heading" data-testid="l5-assign">
      <h3 className="wp-section__title" id="l5-assign-heading">
        {t('incidents.assign.title')}
      </h3>

      <p className="wp-small wp-muted" data-testid="l5-assign-owner-note">
        {t('incidents.assign.ownerNote')}
      </p>

      <div className="wp-field">
        <label className="wp-field__label" htmlFor="l5-assign-owner">
          {t('incidents.assign.owner')}
        </label>
        <input
          className="wp-field__input"
          id="l5-assign-owner"
          data-testid="l5-assign-owner"
          type="text"
          inputMode="numeric"
          value={owner}
          autoComplete="off"
          onChange={(event) => {
            setOwner(event.target.value);
          }}
        />
      </div>

      <div className="wp-field">
        <label className="wp-field__label" htmlFor="l5-assign-severity">
          {t('incidents.assign.severity')}
        </label>
        <select
          className="wp-field__input"
          id="l5-assign-severity"
          data-testid="l5-assign-severity"
          value={grade}
          onChange={(event) => {
            setGrade(event.target.value as AlertSeverity);
          }}
        >
          {ALERT_SEVERITIES.map((value) => (
            <option key={value} value={value}>
              {t(SEVERITY_LABEL[value])}
            </option>
          ))}
        </select>
        {/* The one sentence a re-grade needs, and it is not obvious from the control:
            the deadline is recomputed from `created_at`, so moving this dial never
            buys a fresh budget, and an incident opened long enough ago is overdue the
            moment it is upgraded. */}
        <p className="wp-small wp-muted" data-testid="l5-assign-regrade">
          {t('incidents.assign.regrade')}
        </p>
      </div>

      {etag === null ? (
        // Absent, not disabled. There is nothing wrong with this operator's
        // permissions; the client simply does not hold the precondition the endpoint
        // requires, and a greyed button would make the wrong claim about which.
        <StatusNote
          severity="medium"
          title={t('incidents.assign.noEtag.title')}
          testId="l5-assign-no-etag"
        >
          <p>{t('incidents.assign.noEtag.body')}</p>
        </StatusNote>
      ) : (
        <div className="wp-note__actions">
          <button
            className="wp-button wp-button--primary"
            type="button"
            data-testid="l5-assign-submit"
            disabled={pending}
            onClick={() => {
              onSubmit({
                owner_id: owner.trim() === '' ? null : owner.trim(),
                severity: grade,
              });
            }}
          >
            {pending ? t('incidents.assign.saving') : t('incidents.assign.save')}
          </button>
        </div>
      )}

      {error === null ? null : (
        <>
          <ApiErrorNote
            error={error}
            subject={t('incidents.subjectWord')}
            testId="l5-assign-error"
          />
          <p className="wp-small" data-testid="l5-assign-kept">
            {t('incidents.assign.kept')}
          </p>
          {conflictEtag === null ? null : (
            <p className="wp-small wp-muted" data-testid="l5-assign-conflict-etag">
              {t('incidents.assign.conflict', { etag: conflictEtag })}
            </p>
          )}
        </>
      )}

      {saved ? (
        <StatusNote severity="low" title={t('incidents.assign.saved')} testId="l5-assign-ok" />
      ) : null}
    </section>
  );
}
